// Navigation und Zugriff entstehen aus den wirksamen Rechten (`permissions` aus /api/v1/me),
// nie aus Rollennamen. Die Rechte-Codes stammen aus dem Rechtekatalog des Backends.
import {
  Calendar,
  CalendarDays,
  ClipboardList,
  FileCheck2,
  FileText,
  FileUp,
  Gauge,
  Hammer,
  Inbox,
  Layers,
  ListChecks,
  Map as MapIcon,
  Timer,
  UserCog,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react'

export type NavId =
  | 'tasks'
  | 'inbox'
  | 'orders'
  | 'imports'
  | 'map'
  | 'areas'
  | 'dispatch'
  | 'calendar'
  | 'review'
  | 'permits'
  | 'pits'
  | 'deadlines'
  | 'crews'
  | 'users'
  | 'dashboard'
  | 'forms'

export type AppPath =
  | '/tasks'
  | '/inbox'
  | '/orders'
  | '/imports/otw'
  | '/map'
  | '/areas'
  | '/dispatch'
  | '/calendar'
  | '/review'
  | '/permits'
  | '/pits'
  | '/deadlines'
  | '/crews'
  | '/users'
  | '/dashboard'
  | '/admin/forms'

export interface NavItem {
  id: NavId
  to: AppPath
  icon: LucideIcon
  /** Sichtbar, wenn mindestens eines dieser Rechte vorliegt. Leer = jeder angemeldete Nutzer. */
  anyOf: readonly string[]
}

/** Reihenfolge = Reihenfolge im Menü. */
export const NAV_ITEMS: readonly NavItem[] = [
  {
    id: 'tasks',
    to: '/tasks',
    icon: ListChecks,
    anyOf: ['order.assign', 'order.execute', 'order.accept'],
  },
  { id: 'inbox', to: '/inbox', icon: Inbox, anyOf: [] },
  { id: 'orders', to: '/orders', icon: ClipboardList, anyOf: ['order.read'] },
  { id: 'imports', to: '/imports/otw', icon: FileUp, anyOf: ['order.create'] },
  // Disposition (nur Rechte, die nur der GU hat)
  { id: 'map', to: '/map', icon: MapIcon, anyOf: ['order.assign'] },
  { id: 'areas', to: '/areas', icon: Layers, anyOf: ['order.assign'] },
  { id: 'dispatch', to: '/dispatch', icon: CalendarDays, anyOf: ['order.assign'] },
  { id: 'calendar', to: '/calendar', icon: Calendar, anyOf: ['order.assign'] },
  // Prüfer
  { id: 'review', to: '/review', icon: FileCheck2, anyOf: ['measurement.approve'] },
  { id: 'permits', to: '/permits', icon: FileText, anyOf: ['permit.manage'] },
  // Bauleiter
  { id: 'pits', to: '/pits', icon: Hammer, anyOf: ['pit.manage'] },
  { id: 'deadlines', to: '/deadlines', icon: Timer, anyOf: ['deadline.read'] },
  // Sub-Administration
  { id: 'crews', to: '/crews', icon: Users, anyOf: ['crew.manage'] },
  { id: 'users', to: '/users', icon: UserCog, anyOf: ['user.manage'] },
  // Auftraggeber-Lesezugang
  { id: 'dashboard', to: '/dashboard', icon: Gauge, anyOf: ['report.read'] },
  // Administration: Formulardefinitionen
  { id: 'forms', to: '/admin/forms', icon: Wrench, anyOf: ['form.manage'] },
]

/** Erste passende Seite je Rollenprofil, wenn `/` aufgerufen wird. */
const HOME_PRIORITY: readonly NavId[] = [
  'tasks',
  'review',
  'pits',
  'users',
  'dashboard',
  'orders',
  'inbox',
]

export function hasAnyPermission(
  permissions: readonly string[],
  anyOf: readonly string[],
): boolean {
  return anyOf.length === 0 || anyOf.some((permission) => permissions.includes(permission))
}

function byId(id: NavId): NavItem {
  const item = NAV_ITEMS.find((candidate) => candidate.id === id)
  if (!item) throw new Error(`Unbekannte Navigation: ${id}`)
  return item
}

export function canAccess(permissions: readonly string[], id: NavId): boolean {
  return hasAnyPermission(permissions, byId(id).anyOf)
}

export function visibleNavItems(permissions: readonly string[]): NavItem[] {
  return NAV_ITEMS.filter((item) => hasAnyPermission(permissions, item.anyOf))
}

export function homePath(permissions: readonly string[]): AppPath {
  const id = HOME_PRIORITY.find((candidate) => canAccess(permissions, candidate)) ?? 'inbox'
  return byId(id).to
}
